import { Extends } from "@common/helpers/typescript";
import { FC, useCallback, useMemo } from "react";
import { useUser } from "@hooks/useUser";
import { useEntities } from "@hooks/useEntities";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { FormikProvider, useFormik } from "formik";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useUpdateUserMutation } from "@api/user.api";
import { useMobileBlockScreen } from "@hooks/useMobileView";
import { LogoutIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useLogout } from "@hooks/useLogout";
import { PlanManagementProvider } from "@components/billing/PlanManagement/PlanManagementProvider";
import { useManageSteps } from "../useManageSteps";
import { OnboardingStepKey } from "../steps";
import {
    InviteUserProfileFlowFormSchema,
    inviteUserProfileFormSchema,
} from "../lib";
import { ProductPreview } from "../components/ProductPreview";
import { InviteProfile } from "../steps/InviteProfile";
import { Outro } from "../steps/Outro/Outro";
import { ConnectAccounts } from "../steps/ConnectAccounts/ConnectAccounts";
import { ConnectAccountsSidebar } from "../steps/ConnectAccounts/ConnectAccountsSidebar";
import { OnboardingMobileBlock } from "../steps/OnboardingMobileBlock";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import containerStyles from "../components/BaseOnboardingContainer.module.scss";
import { Subscribe } from "../steps/Subscribe";

type BillingOwnerInviteFlowSteps = Extends<
    OnboardingStepKey,
    "inviteProfile" | "connectAccounts" | "subscribe" | "outro" | "mobileBlock"
>;

interface BillingOwnerInviteFlowProps {
    onFinish: () => void;
}

export const BillingOwnerInviteFlow: FC<BillingOwnerInviteFlowProps> = ({
    onFinish,
}) => {
    const logout = useLogout();
    const isMobile = useMobileBlockScreen();
    const user = useUser();
    const entities = useEntities();
    const workspaceEntitiesIds = useMemo(
        () => entities.map((e) => e.id),
        [entities],
    );
    const financialAccounts = useFinancialAccounts({
        entityIds: workspaceEntitiesIds,
    });
    const { isFetched: billingStatusFetched, isSubscribed } =
        useBillingStatus();

    const updateUserMutation = useUpdateUserMutation();

    const getInitialStep = useCallback((): BillingOwnerInviteFlowSteps => {
        if (!user.preferredName) {
            return "inviteProfile";
        }

        if (isMobile) {
            return "mobileBlock";
        }

        if (isSubscribed) {
            return "outro";
        }

        if (!financialAccounts.length) {
            return "connectAccounts";
        }

        if (!isSubscribed) {
            return "subscribe";
        }

        return "outro";
    }, [user.preferredName, financialAccounts.length, isSubscribed, isMobile]);

    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<BillingOwnerInviteFlowSteps>(
            billingStatusFetched,
            getInitialStep,
        );

    const formik = useFormik<InviteUserProfileFlowFormSchema>({
        initialValues: {
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
        },
        validate: validateFormikWithZodSchema(inviteUserProfileFormSchema),
        onSubmit: submitHelper({
            handler: async ({ firstName, lastName }) => {
                await updateUserMutation.mutateAsync({
                    preferredName: `${firstName} ${lastName}`,
                });

                if (isMobile) {
                    go("mobileBlock");
                    return;
                }

                go("connectAccounts");
            },
        }),
    });

    let main: React.ReactNode;
    let sidebar: React.ReactNode;

    switch (currentStep) {
        case "inviteProfile":
            main = <InviteProfile busy={formik.isSubmitting} />;
            sidebar = <ProductPreview />;
            break;
        case "connectAccounts":
            main = (
                <ConnectAccounts
                    onBack={() => go("inviteProfile")}
                    onFinished={() => go("subscribe")}
                    allowNoAccounts
                    buttonSize="lg"
                />
            );
            sidebar = <ConnectAccountsSidebar />;
            break;
        case "subscribe":
            main = (
                <PlanManagementProvider>
                    <Subscribe onContinue={() => go("outro")} />
                </PlanManagementProvider>
            );
            break;
        case "outro":
            main = <Outro onFinished={onFinish} />;
            break;
        case "mobileBlock":
            main = <OnboardingMobileBlock heading="Account created" />;
            break;
    }

    return (
        <>
            <FormikProvider value={formik}>
                <OnboardingLayout
                    currentStep={currentStep}
                    nextStep={nextStep}
                    isExiting={isExiting}
                    main={main}
                    sidebar={sidebar}
                />
            </FormikProvider>

            <Tooltip content="Log out">
                <Button
                    variant="secondary"
                    size="lg"
                    className={containerStyles.close}
                    onClick={logout}
                    iconOnly
                    aria-label="Log out"
                >
                    <LogoutIcon />
                </Button>
            </Tooltip>
        </>
    );
};
